import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AvatarSelector } from '@/components/ui/AvatarSelector';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { User, Save, Lock, Moon, Sun, ChevronDown, Info } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTheme } from 'next-themes';
import { PWAInstallCard } from '@/components/layout/PWAInstallCard';

export function ProfileSettingsTab() {
  const { user, updatePassword } = useAuth();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [accountInfoOpen, setAccountInfoOpen] = useState(false);
  
  const [profile, setProfile] = useState({
    display_name: '',
    avatar_url: '',
    avatar_bg_color: '#E5E7EB'
  });
  
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          return;
        }

        if (data) {
          let normalizedAvatarUrl = data.avatar_url || '';
          if (normalizedAvatarUrl && !normalizedAvatarUrl.startsWith('avatars/')) {
            const fileName = normalizedAvatarUrl.split('/').pop() || '';
            if (fileName && !fileName.includes('?') && (fileName.endsWith('.png') || fileName.endsWith('.jpg') || fileName.endsWith('.jpeg'))) {
              normalizedAvatarUrl = `avatars/${fileName}`;
            } else {
              normalizedAvatarUrl = 'avatars/1.png';
            }
          }
          
          setProfile({
            display_name: data.display_name || '',
            avatar_url: normalizedAvatarUrl,
            avatar_bg_color: data.avatar_bg_color || '#E5E7EB'
          });
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };

    fetchProfile();
  }, [user]);

  const getInitials = () => {
    if (profile.display_name) {
      return profile.display_name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          email: user.email || '',
          display_name: profile.display_name.trim() || null,
          avatar_url: profile.avatar_url.trim() || null,
          avatar_bg_color: profile.avatar_bg_color
        });

      if (error) throw error;

      toast({
        title: 'Profiel bijgewerkt',
        description: 'Je profiel is succesvol bijgewerkt.',
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Fout bij opslaan',
        description: 'Er ging iets mis bij het bijwerken van je profiel.',
        variant: 'destructive'
      });
    }
    setLoading(false);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword.length < 6) {
      toast({
        title: 'Wachtwoord te kort',
        description: 'Je wachtwoord moet minimaal 6 karakters bevatten.',
        variant: 'destructive'
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Wachtwoorden komen niet overeen',
        description: 'Controleer je wachtwoorden en probeer opnieuw.',
        variant: 'destructive'
      });
      return;
    }

    setPasswordLoading(true);
    try {
      await updatePassword(passwordData.newPassword);
      toast({
        title: 'Wachtwoord gewijzigd',
        description: 'Je wachtwoord is succesvol gewijzigd.',
      });
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      // Error already handled in useAuth
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="grid gap-4 sm:gap-6">
      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profiel instellingen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-6">
            <AvatarSelector
              selectedAvatar={profile.avatar_url}
              selectedBgColor={profile.avatar_bg_color}
              onAvatarChange={(avatar) => setProfile(prev => ({ ...prev, avatar_url: avatar }))}
              onBgColorChange={(color) => setProfile(prev => ({ ...prev, avatar_bg_color: color }))}
              fallbackText={getInitials()}
            />

            <div>
              <Label htmlFor="display_name">Weergavenaam</Label>
              <Input
                id="display_name"
                value={profile.display_name}
                onChange={(e) => setProfile(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="Je volledige naam"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Deze naam wordt gebruikt in de interface en in communicatie
              </p>
            </div>

            <div>
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-muted"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Je e-mailadres kan niet worden gewijzigd
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={loading}>
                <Save className="mr-2 h-4 w-4" />
                {loading ? 'Opslaan...' : 'Profiel opslaan'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Wachtwoord wijzigen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="new_password">Nieuw wachtwoord</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Minimaal 6 karakters"
                required
              />
            </div>

            <div>
              <Label htmlFor="confirm_password">Bevestig nieuw wachtwoord</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Herhaal je nieuwe wachtwoord"
                required
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={passwordLoading}>
                <Save className="mr-2 h-4 w-4" />
                {passwordLoading ? 'Wijzigen...' : 'Wachtwoord wijzigen'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Appearance & PWA in 2-column grid on larger screens */}
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
        {/* Dark Mode Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              Weergave
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">Dark Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Schakel tussen licht en donker thema
                </p>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              />
            </div>
          </CardContent>
        </Card>

        {/* PWA Install */}
        <PWAInstallCard />
      </div>

      {/* Account Information - Collapsible */}
      <Collapsible open={accountInfoOpen} onOpenChange={setAccountInfoOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Account informatie
                </div>
                <ChevronDown className={`h-5 w-5 transition-transform ${accountInfoOpen ? 'rotate-180' : ''}`} />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="space-y-4">
                <div>
                  <Label>Gebruikers-ID</Label>
                  <Input value={user?.id || ''} disabled className="bg-muted font-mono text-sm" />
                </div>
                
                <div>
                  <Label>Account aangemaakt</Label>
                  <Input 
                    value={user?.created_at ? new Date(user.created_at).toLocaleDateString('nl-NL') : ''} 
                    disabled 
                    className="bg-muted" 
                  />
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}
